import { useForm, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { handleErrors } from '@/Shared/ui/form';
import { t } from 'i18next';
import { formTranslation } from '@/Shared/ui/sharedTranslation';
import { useUserData } from '@/User/ui/context';
import { CreateProfileFormValues, ModalMode, ProfileType, UpdateProfileData } from '../../domain/ProfileTypes';
import { AccessRolesModel } from '@/AccessRoles/domain';





export const useCreateProfileModal = (
  onClose: () => void, 
  mode: ModalMode = 'create',
  profileData?: AccessRolesModel
) => {
  const { createProfile, updateProfile } = useUserData();
  const maxAllowed = t(formTranslation.errors.maxAllowed);
  const schema = yup.object().shape({
  name: yup.string()
    .required(t(formTranslation.errors.fieldRequired))
    .max(20, maxAllowed + ' 20'),
  surname: yup.string()
    .required(t(formTranslation.errors.fieldRequired))
    .max(20, maxAllowed + ' 20'),
  surname2: yup.string()
    .max(20, maxAllowed + ' 20'),
  email: yup.string()
    .required(t(formTranslation.errors.fieldRequired))
    .max(75, maxAllowed + ' 75')
    .test('strict-email', t(formTranslation.errors.invalidFormat), (value) => {
      if (!value) return false;
      
      // Verificar que no tenga tildes en todo el email
      const hasAccents = /[áéíóúÁÉÍÓÚñÑüÜçÇàèìòùÀÈÌÒÙ]/.test(value);
      if (hasAccents) return false;
      
      // Verificar que no tenga dominios Punycode (xn--)
      const hasPunycode = /xn--/.test(value);
      if (hasPunycode) return false;
      
      // Verificar formato con regex estricto
      return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/.test(value);
    }),
  rolName: yup.string().oneOf(['Admin', 'Visor', 'SuperAdmin']).required('Selecciona un perfil'),
});

const schemaEdit = yup.object().shape({
  name: yup.string(),
  surname: yup.string(),
  surname2: yup.string(),
  email: yup.string()
    .test('strict-email', t(formTranslation.errors.invalidFormat), (value) => {
      if (!value) return true; // Optional in edit mode
      
      // Verificar que no tenga tildes en todo el email
      const hasAccents = /[áéíóúÁÉÍÓÚñÑüÜçÇàèìòùÀÈÌÒÙ]/.test(value);
      if (hasAccents) return false;
      
      // Verificar que no tenga dominios Punycode (xn--)
      const hasPunycode = /xn--/.test(value);
      if (hasPunycode) return false;
      
      // Verificar formato con regex estricto
      return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/.test(value);
    }),
  rolName: yup.string().oneOf(['Admin', 'Visor', 'SuperAdmin']).required('Selecciona un perfil'),
});
  
  const defaultValues = mode === 'edit' && profileData ? {
    name: profileData.name,
    surname: profileData.surname,
    surname2: profileData.surname2,
    email: profileData.email,
    rolName: profileData.rolName as ProfileType,
  } : {
    name: '',
    surname: '',
    surname2: '',
    email: '',
    rolName: 'Visor' as ProfileType,
  };
  
  const form = useForm<CreateProfileFormValues>({
    resolver: yupResolver(mode === 'edit' ? schemaEdit : schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    shouldFocusError: true,
    shouldUnregister: true,
    defaultValues,
  });

  const { errors } = handleErrors(form.formState);
  const rolName = form.watch('rolName') || defaultValues.rolName;

  const onFormSubmit: SubmitHandler<CreateProfileFormValues> = async (data) => {
    if (mode === 'edit' && profileData?.userName) {
      const updateData: UpdateProfileData = {
        userName: profileData.userName,
        roleName: data.rolName,
      }
      await updateProfile(updateData);
    } else {
      await createProfile(data);
    }
    onClose();
  };

  return {
    form,
    errors,
    rolName,
    onFormSubmit,
  };
};